import { createDataProcessorSlice } from './dataProcessorSlice';
import { createRebalanceSchedulerSlice } from './rebalanceSchedulerSlice';
import { createSecurityScoringSlice } from './securityScoringSlice';
import { createWeightingMethodSlice } from './weightingMethodSlice';
import type {
  DefinitionFormStoreState,
  UtilitySlice,
} from '../types/definitionFormStoreTypes';
import type { StateCreator } from 'zustand';
import { DEFAULT_TRANSACTION_COSTS } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/WeightingMethod/types';

const TOTAL_STEPS = 4;

export const createUtilitySlice: StateCreator<
  DefinitionFormStoreState,
  [],
  [],
  UtilitySlice
> = (set, get, store) => ({
  currentStep: 0,
  totalSteps: TOTAL_STEPS,
  isPreviewOpen: false,

  setCurrentStep: (step: number) => {
    if (step >= 0 && step < TOTAL_STEPS) {
      set({ currentStep: step });
    }
  },

  goToNextStep: () => {
    const { currentStep } = get();
    if (currentStep < TOTAL_STEPS) {
      set({ currentStep: currentStep + 1 });
    }
  },

  goToPreviousStep: () => {
    const { currentStep } = get();
    if (currentStep > 0) {
      set({ currentStep: currentStep - 1 });
    }
  },

  togglePreview: () => {
    set({ isPreviewOpen: !get().isPreviewOpen });
  },

  reset: () => {
    const defaultDataProcessor = createDataProcessorSlice(set, get, store);
    const defaultRebalanceScheduler = createRebalanceSchedulerSlice(
      set,
      get,
      store,
    );
    const defaultSecurityScoring = createSecurityScoringSlice(set, get, store);
    const defaultWeightingMethod = createWeightingMethodSlice(set, get, store);

    set({
      currentStep: 0,
      data_processor: defaultDataProcessor.data_processor,
      rebalance_scheduler: defaultRebalanceScheduler.rebalance_scheduler,
      schedulerSeededUniverse:
        defaultRebalanceScheduler.schedulerSeededUniverse,
      steps: defaultSecurityScoring.steps,
      countryDecomposition: defaultSecurityScoring.countryDecomposition,
      gen_config: defaultWeightingMethod.gen_config,
      weightingCategory: defaultWeightingMethod.weightingCategory,
      weightingParams: defaultWeightingMethod.weightingParams,
      weightingSelectedMetrics: defaultWeightingMethod.weightingSelectedMetrics,
      concentrationLimits: defaultWeightingMethod.concentrationLimits,
      taxLossHarvesting: defaultWeightingMethod.taxLossHarvesting,
      transactionCosts: defaultWeightingMethod.transactionCosts,
      totalCapital: defaultWeightingMethod.totalCapital,
    });
  },

  getSubmissionPayload: () => {
    const state = get();
    return {
      data_processor: state.data_processor,
      rebalance_scheduler: state.rebalance_scheduler,
      steps: state.steps,
      countryDecomposition: state.countryDecomposition,
      gen_config: state.gen_config,
      weightingCategory: state.weightingCategory,
      weightingParams: state.weightingParams,
      weightingSelectedMetrics: state.weightingSelectedMetrics,
      concentrationLimits: state.concentrationLimits,
      taxLossHarvesting: state.taxLossHarvesting,
      transactionCosts: state.transactionCosts,
      totalCapital: state.totalCapital,
    };
  },

  loadFromPayload: (payload, step = 0) => {
    set({
      currentStep: step,
      data_processor: payload.data_processor,
      rebalance_scheduler: payload.rebalance_scheduler,
      // Mark the loaded universe as already-seeded so useUniverseSync's
      // auto-seed effect does NOT overwrite the loaded schedule with the
      // universe's default rebalancer (which re-enables Daily rebalancing and
      // clobbers a generated is_rebal_activated=false). Without this, opening a
      // Soteria strategy that disabled rebalancing shows it toggled back On.
      schedulerSeededUniverse:
        payload.data_processor?.universe?.portfolio_id ?? '',
      steps: payload.steps,
      countryDecomposition: payload.countryDecomposition ?? {
        isEnabled: false,
        method: 'index_weight',
        minWeightPct: null,
        customWeights: [],
      },
      gen_config: payload.gen_config,
      weightingCategory: payload.weightingCategory ?? null,
      weightingParams: payload.weightingParams ?? {},
      weightingSelectedMetrics: payload.weightingSelectedMetrics ?? [],
      concentrationLimits: payload.concentrationLimits,
      taxLossHarvesting: payload.taxLossHarvesting,
      // A payload from before the block existed carries none: no cost modelled.
      transactionCosts: payload.transactionCosts ?? DEFAULT_TRANSACTION_COSTS,
      totalCapital: payload.totalCapital,
    });
  },
});
